import { useState, useCallback, useRef, useEffect } from 'react';
import { getBlockedAttachmentExtension } from '../utils/attachmentPolicy';

/**
 * Custom hook for managing file attachment state and operations
 * Handles file selection, upload lifecycle, and cancellation
 */
const useFileAttachment = (options = {}) => {
  const {
    clearOnRoomChange = true,
    currentRoomId = null,
    onFileRejected = () => {},
  } = options;

  // States
  const [attachment, setAttachment] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadController, setUploadController] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(-1); // -1 = not started, 0-100 = progress
  
  // Refs
  const fileInputRef = useRef(null);
  const prevRoomIdRef = useRef(currentRoomId);

  // Clear attachment when room changes (if enabled)
  useEffect(() => {
    if (!clearOnRoomChange) return;

    const hasRoomChanged = prevRoomIdRef.current !== currentRoomId;
    
    if (attachment && hasRoomChanged) {
      setAttachment(null);
    }
    
    prevRoomIdRef.current = currentRoomId;
  }, [currentRoomId, attachment, clearOnRoomChange]);

  // Upload lifecycle handlers
  const handleStartUpload = useCallback((controller) => {
    setIsUploading(true);
    setUploadController(controller);
  }, []);

  const handleStopUpload = useCallback(() => {
    setIsUploading(false);
    setUploadController(null);
    setUploadProgress(-1); // Reset progress
    setAttachment(null); // Clear attachment when upload completes
  }, []);

  const handleCancelUpload = useCallback(() => {
    if (uploadController) {
      uploadController.abort();
    }
    handleStopUpload();
  }, [uploadController, handleStopUpload]);

  // Attachment management
  const handleClearAttachment = useCallback(() => {
    setAttachment(null);
  }, []);

  const handleSetAttachment = useCallback((file) => {
    setAttachment(file);
  }, []);

  // File selection handlers
  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);

    if (files.length > 0) {
      const file = files[0];
      // Security: reject dangerous file types by extension (virus/malware risk).
      // Rejection is surfaced by the caller via onFileRejected (which shows a modal).
      const blockedExt = getBlockedAttachmentExtension(file?.name);
      if (blockedExt) {
        onFileRejected(file, blockedExt);
      } else {
        setAttachment(file);
      }
    }

    // Reset the input so the same file can be selected again
    event.target.value = '';
  }, [onFileRejected]);

  const handleTriggerFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Reset all states
  const resetAttachment = useCallback(() => {
    setAttachment(null);
    setIsUploading(false);
    setUploadController(null);
    setUploadProgress(-1);
  }, []);

  return {
    // States
    attachment,
    isUploading,
    uploadController,
    uploadProgress,
    
    // Refs
    fileInputRef,
    
    // Upload lifecycle
    handleStartUpload,
    handleStopUpload,
    handleCancelUpload,
    setUploadProgress,
    
    // Attachment management
    handleClearAttachment,
    handleSetAttachment,
    resetAttachment,
    
    // File selection
    handleFileSelect,
    handleTriggerFileSelect,
  };
};

export default useFileAttachment;